<script setup>
import { computed, watch, nextTick, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  open: { type: Boolean, default: false },
  searching: { type: Boolean, default: false },
});
const emit = defineEmits(['search']);
const panel = ref(null);
watch(
  () => props.open,
  async open => {
    if (!open) return;
    await nextTick();
    panel.value?.querySelector('input')?.focus();
  }
);

const MIN_PHONE_DIGITS = 4;
const MIN_NAME_LENGTH = 3;

const value = defineModel({ type: String, default: '' });
// Text with letters searches the contact name; otherwise only phone digits count.
const term = computed(() => {
  const text = value.value.trim().replace(/\s+/g, ' ');
  if (/\p{L}/u.test(text)) {
    return text.length >= MIN_NAME_LENGTH ? text : '';
  }
  const digits = text.replace(/[^0-9]/g, '');
  return digits.length >= MIN_PHONE_DIGITS ? digits : '';
});
let lastSearch = '';

watch(term, (search, previous, onCleanup) => {
  if (!search) {
    if (lastSearch) emit('search', '');
    lastSearch = '';
    return;
  }
  const timer = setTimeout(() => {
    if (lastSearch === search) return;
    lastSearch = search;
    emit('search', search);
  }, 350);
  onCleanup(() => clearTimeout(timer));
});
</script>

<template>
  <div
    v-show="open"
    id="conversation-phone-search"
    ref="panel"
    class="mx-3 my-2 rounded-xl border border-n-weak bg-n-solid-2 p-3"
  >
    <div
      class="mb-2 flex items-center gap-2 text-xs font-medium text-n-slate-12"
    >
      <span class="i-hugeicons-search-01 size-4 shrink-0 text-n-brand" />
      {{ $t('CHAT_LIST.PHONE_FILTER') }}
    </div>
    <div class="flex items-center gap-2">
      <Input
        v-model="value"
        type="text"
        size="sm"
        class="min-w-0 flex-1"
        :aria-label="$t('CHAT_LIST.PHONE_FILTER')"
        :placeholder="$t('CHAT_LIST.PHONE_SEARCH_PLACEHOLDER')"
        @keydown.esc="value = ''"
      />
      <Button
        v-if="value"
        icon="i-lucide-x"
        :aria-label="$t('CHAT_LIST.PHONE_SEARCH_CLEAR')"
        :title="$t('CHAT_LIST.PHONE_SEARCH_CLEAR')"
        xs
        ghost
        slate
        @click="value = ''"
      />
    </div>
    <div
      class="mt-2 h-0.5 overflow-hidden rounded-full bg-n-alpha-2 transition-opacity duration-300"
      :class="searching ? 'opacity-100' : 'opacity-0'"
      role="progressbar"
      :aria-hidden="!searching"
      :aria-label="$t('CHAT_LIST.CONTACT_SEARCHING')"
    >
      <div
        class="contact-search-progress h-full w-1/3 rounded-full bg-n-brand/70"
      />
    </div>
  </div>
</template>

<style scoped>
.contact-search-progress {
  animation: contact-search-progress 1.1s ease-in-out infinite;
}

@keyframes contact-search-progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(300%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .contact-search-progress {
    width: 100%;
    animation: none;
  }
}
</style>
